import { api } from './api';
import type {
  LeadTodo,
  LeadTodoEvent,
  TodoAssignee,
  LeadTodoPriority,
  LeadTodoStatus
} from './types';

export type LeadTodoPayload = {
  title: string;
  description: string | null;
  status: LeadTodoStatus;
  priority: LeadTodoPriority;
  due_at: string | null;
  assigned_to: string | null;
  sync_to_calendar: boolean;
};

async function handleResponse<T>(res: Response, fallbackError: string): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || fallbackError);
  }
  return (await res.json()) as T;
}

export async function fetchLeadTodos(leadId: string): Promise<LeadTodo[]> {
  const res = await api(`/api/leads/${leadId}/todos`);
  return handleResponse(res, 'Kunne ikke hente opgaver');
}

export async function createLeadTodo(
  leadId: string,
  payload: LeadTodoPayload
): Promise<LeadTodo> {
  const res = await api(`/api/leads/${leadId}/todos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return handleResponse(res, 'Kunne ikke oprette opgave');
}

export async function updateLeadTodo(
  leadId: string,
  todoId: string,
  payload: LeadTodoPayload
): Promise<LeadTodo> {
  const res = await api(`/api/leads/${leadId}/todos/${todoId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return handleResponse(res, 'Kunne ikke opdatere opgave');
}

export async function updateLeadTodoStatus(
  leadId: string | null,
  todoId: string,
  status: LeadTodoStatus
): Promise<LeadTodo> {
  const endpoint = leadId ? `/api/leads/${leadId}/todos/${todoId}` : `/api/leads/todos/${todoId}`;
  const res = await api(endpoint, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status })
  });
  return handleResponse(res, 'Kunne ikke opdatere status');
}

export type StandaloneTodoPayload = LeadTodoPayload & { lead_id?: string | null };

export async function createStandaloneTodo(payload: StandaloneTodoPayload): Promise<LeadTodo> {
  const res = await api('/api/leads/todos', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return handleResponse(res, 'Kunne ikke oprette opgave');
}

export async function updateStandaloneTodo(
  todoId: string,
  payload: StandaloneTodoPayload
): Promise<LeadTodo> {
  const res = await api(`/api/leads/todos/${todoId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return handleResponse(res, 'Kunne ikke opdatere opgave');
}

export async function fetchLeadTodoEvents(
  leadId: string,
  todoId: string
): Promise<LeadTodoEvent[]> {
  const res = await api(`/api/leads/${leadId}/todos/${todoId}/events`);
  return handleResponse(res, 'Kunne ikke hente historik');
}

export async function fetchTodoAssignees(): Promise<TodoAssignee[]> {
  const res = await api('/api/leads/assignees');
  return handleResponse(res, 'Kunne ikke hente brugere');
}

export async function fetchAllLeadTodos(filters: LeadTodoListFilters = {}): Promise<LeadTodo[]> {
  const params = new URLSearchParams();
  if (filters.status && filters.status.length) {
    params.set('status', filters.status.join(','));
  }
  if (filters.assigned_to) {
    params.set('assigned_to', filters.assigned_to);
  }
  if (filters.search) {
    params.set('search', filters.search);
  }
  if (filters.includeCompleted) {
    params.set('includeCompleted', 'true');
  }
  if (filters.overdueOnly) {
    params.set('overdueOnly', 'true');
  }
  const query = params.toString() ? `?${params.toString()}` : '';
  const res = await api(`/api/leads/todos${query}`);
  return handleResponse(res, 'Kunne ikke hente opgaver');
}

export type LeadTodoListFilters = {
  status?: LeadTodoStatus[];
  assigned_to?: string | null;
  search?: string;
  includeCompleted?: boolean;
  overdueOnly?: boolean;
};
